// src/annihilation/AbilityFX.js
// ============================================================
//  ANNIHILATION — ABILITY VISUALS (one source of truth).
//
//  "What does Phantom Strike LOOK like when it fires?" is
//  answered HERE and nowhere else. Every zone, dungeon, and
//  yard renders combat visuals through this file — so the move
//  you throw in Sigfer's Yard is the move you recognize in the
//  Grove and the Fold. (The July 16 continuity law, applied to
//  the strike itself.)
//
//  VYREN PATTERN: the registry is keyed by INTERNAL ability
//  ids — beings.js lytAttack ids (phantom_strike, dragons_bite,
//  blade_cycle, psychic_lance) and enemyTaxonomy boss attack
//  ids (phantom_lunge, claw_sweep, spectral_roar,
//  haunting_presence). A caller never invents a key; it passes
//  the id it already owns. When the skill-cast pass makes
//  Primera/Impera/U'hlti castable, each new move is ONE new
//  registry entry here — nothing else in the game changes.
//  (Note: haunting_presence is ONE key on purpose. The
//  Matriarch is Sael'esh; when the player's U'hlti fires one
//  day, it fires HER move, and it looks like her move.)
//
//  THREE EXPORTS:
//  · useAbilityFX     — the hook a zone mounts once. Returns
//                       { fx, spawnFx }; spawned effects expire
//                       themselves.
//  · AbilityFXLayer   — renders the live fx list in world
//                       space. Mount inside the world div,
//                       above enemies, below floating numbers.
//  · (registry)       — FX_DEFS, internal. Colors take a being
//                       palette (getBeingPalette) as `tint`, so
//                       YOUR strikes are always your being's
//                       colors — a tiger rakes teal, a dragon
//                       bites ember.
//
//  All visuals generated (Batch 4.5 doctrine) — pure CSS, zero
//  assets, deterministic. Timings/colors are dials.
// ============================================================

import React, { useCallback, useState } from "react";

// ── Keyframes — imperative, id-guarded (house law: no JSX <style>) ──
if (typeof document !== "undefined" && !document.getElementById("anni-fx-kf")) {
  const el = document.createElement("style");
  el.id = "anni-fx-kf";
  el.textContent = `
@keyframes anniFxRake   { 0%{transform:scaleX(0.15);opacity:0} 20%{opacity:1} 100%{transform:scaleX(1.12);opacity:0} }
@keyframes anniFxJawTop { 0%{transform:translateY(-46%);opacity:1} 55%{transform:translateY(4%)} 100%{transform:translateY(0);opacity:0} }
@keyframes anniFxJawBot { 0%{transform:translateY(46%);opacity:1} 55%{transform:translateY(-4%)} 100%{transform:translateY(0);opacity:0} }
@keyframes anniFxEmber  { 0%{transform:translate(0,0) scale(1);opacity:1} 100%{transform:translate(var(--ex,8px),-16px) scale(0.35);opacity:0} }
@keyframes anniFxSweep  { 0%{transform:rotate(var(--a0,-140deg));opacity:0} 18%{opacity:1} 100%{transform:rotate(var(--a1,40deg));opacity:0} }
@keyframes anniFxBeam   { 0%{transform:scaleX(0);opacity:0.95} 55%{transform:scaleX(1);opacity:1} 100%{transform:scaleX(1);opacity:0} }
@keyframes anniFxRing   { 0%{transform:scale(0.2);opacity:0.9} 100%{transform:scale(1);opacity:0} }
@keyframes anniFxGhost  { 0%{opacity:0.7} 100%{opacity:0} }
@keyframes anniFxPuff   { 0%{transform:translateY(0) scale(0.6);opacity:0.85} 100%{transform:translateY(-14px) scale(1.35);opacity:0} }
@keyframes anniFxSpark  { 0%{transform:scale(0.3);opacity:1} 100%{transform:scale(1.3);opacity:0} }
@keyframes anniFxDrip   { 0%{transform:translateY(0);opacity:0.95} 100%{transform:translateY(15px);opacity:0} }
@keyframes anniFxRise   { 0%{transform:translateY(6px);opacity:0.9} 100%{transform:translateY(-20px);opacity:0} }
@keyframes anniFxShard  { 0%{transform:translate(0,0) rotate(0deg);opacity:1} 100%{transform:translate(var(--sx,10px),var(--sy,-14px)) rotate(170deg);opacity:0} }
@keyframes anniFxPulse  { 0%{transform:scale(0.35);opacity:0.55} 100%{transform:scale(1);opacity:0} }
@keyframes anniFxStreak { 0%{transform:scaleX(0);opacity:0.95} 65%{transform:scaleX(1);opacity:0.85} 100%{transform:scaleX(1);opacity:0} }
@keyframes anniFxColumn { 0%{opacity:0} 25%{opacity:0.95} 100%{opacity:0} }
`;
  document.head.appendChild(el);
}

// Fallback tint when a caller has no being palette (enemy fx
// mostly carry their own colors and ignore tint).
const DEFAULT_TINT = { main: "#c9a84c", deep: "#241c12", glow: "#f0d060", accent: "#e8d8a8" };

let fxSeq = 0;

// ------------------------------------------------------------
// THE HOOK — mount once per zone. spawnFx(fxId, opts):
//   pos     {x,y} tile coords — the anchor (usually the actor)
//   toward  {x,y} — aim point; sets angle if no explicit angle
//   to      {x,y} — end point (streak/lunge/dodge effects)
//   facing  {x,y} — controller facing; sets angle
//   step    combo step (1-indexed) — swing variants
//   tint    being palette (getBeingPalette) — YOUR colors
//   big     elite/boss scale-up
//   crit    crit flavor (hit_spark)
// Unknown fxId is a silent no-op — a new ability before its
// recipe lands never crashes a zone.
// ------------------------------------------------------------
export function useAbilityFX() {
  const [fx, setFx] = useState([]);

  const spawnFx = useCallback((fxId, opts = {}) => {
    const def = FX_DEFS[fxId];
    if (!def || !opts.pos) return;
    const id = ++fxSeq;
    const angle =
      opts.angle != null
        ? opts.angle
        : opts.toward
        ? Math.atan2(opts.toward.y - opts.pos.y, opts.toward.x - opts.pos.x)
        : opts.facing
        ? Math.atan2(opts.facing.y || 0, opts.facing.x || 0)
        : 0;
    const ev = {
      id,
      fxId,
      pos: { ...opts.pos },
      to: opts.to ? { ...opts.to } : opts.toward ? { ...opts.toward } : null,
      angle,
      step: opts.step || 1,
      tint: opts.tint || null,
      big: Boolean(opts.big),
      crit: Boolean(opts.crit),
    };
    setFx((list) => [...list, ev]);
    setTimeout(() => setFx((list) => list.filter((f) => f.id !== id)), def.ttlMs);
  }, []);

  return { fx, spawnFx };
}

// ------------------------------------------------------------
// THE LAYER — world-space renderer. DOM order gives depth:
// mount after enemies, before floating numbers.
// ------------------------------------------------------------
export function AbilityFXLayer({ fx, tile = 48 }) {
  return (
    <>
      {fx.map((ev) => {
        const def = FX_DEFS[ev.fxId];
        if (!def) return null;
        const R = def.render;
        return <R key={ev.id} ev={ev} tile={tile} />;
      })}
    </>
  );
}

// ── shared position helper — a square box centered on a tile ──
const boxAt = (pos, tile, size) => ({
  position: "absolute",
  left: pos.x * tile + tile / 2 - size / 2,
  top: pos.y * tile + tile / 2 - size / 2,
  width: size,
  height: size,
  pointerEvents: "none",
  zIndex: 6,
});

// ─────────────────────────────────────────────────────────────
// PLAYER LYT ATTACKS — one recipe per being. Rendered rotated
// so the strike rides ahead of the body in the facing direction
// (+x is forward, same convention as the bodies).
// ─────────────────────────────────────────────────────────────

// SAEL'ESH — Phantom Strike. Triple claw-rake in ghost-teal.
// Swing 2 is the phase swipe (blurred, half in the other world).
// Swing 3 (Third Strike) crosses the rakes and flares gold.
function PhantomStrikeFX({ ev, tile }) {
  const t = ev.tint || DEFAULT_TINT;
  const size = tile * 2.6;
  const phase = ev.step === 2;
  const third = ev.step >= 3;
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), transform: `rotate(${ev.angle}rad)` }}>
      <div
        style={{
          position: "absolute", left: "56%", top: "20%", width: "44%", height: "60%",
          animation: "anniFxRake 0.28s ease-out forwards", transformOrigin: "0% 50%",
          filter: phase ? "blur(1.3px)" : "none",
        }}
      >
        {[0, 1, 2].map((i) => (
          <div key={i} style={{
            position: "absolute", top: `${8 + i * 32}%`, left: 0, width: "100%", height: "13%",
            borderRadius: 999,
            background: `linear-gradient(90deg, transparent, ${t.glow}, ${t.main})`,
            boxShadow: `0 0 6px ${t.glow}`, transform: `rotate(${(i - 1) * 7}deg)`,
          }} />
        ))}
        {third
          ? [0, 1, 2].map((i) => (
              <div key={`x${i}`} style={{
                position: "absolute", top: `${8 + i * 32}%`, left: 0, width: "100%", height: "13%",
                borderRadius: 999,
                background: `linear-gradient(90deg, transparent, ${t.accent}, ${t.glow})`,
                boxShadow: `0 0 6px ${t.glow}`, transform: `rotate(${(1 - i) * 24}deg)`,
              }} />
            ))
          : null}
      </div>
      {third ? (
        <div style={{
          position: "absolute", left: "66%", top: "38%", width: "24%", height: "24%",
          borderRadius: "50%", background: "radial-gradient(circle, #ffd24a 0%, transparent 70%)",
          animation: "anniFxSpark 0.3s ease-out forwards",
        }} />
      ) : null}
      {phase ? (
        <div style={{
          position: "absolute", left: "50%", top: "16%", width: "50%", height: "68%",
          borderRadius: "50%", background: `radial-gradient(ellipse, ${t.main}33 0%, transparent 70%)`,
          animation: "anniFxGhost 0.3s linear forwards",
        }} />
      ) : null}
    </div>
  );
}

// XERATH'OUN — Dragon's Bite. Two jaw arcs snap shut; embers
// burst off the hit. Heavy, slow, unmistakable.
function DragonsBiteFX({ ev, tile }) {
  const t = ev.tint || DEFAULT_TINT;
  const size = tile * 2.6;
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), transform: `rotate(${ev.angle}rad)` }}>
      <div style={{
        position: "absolute", left: "58%", top: "18%", width: "38%", height: "30%",
        borderRadius: "50% 50% 0 0", borderTop: `4px solid ${t.glow}`,
        borderLeft: `3px solid ${t.main}88`, borderRight: `3px solid ${t.main}88`,
        boxShadow: `0 0 8px ${t.glow}66`,
        animation: "anniFxJawTop 0.32s ease-in forwards",
      }} />
      <div style={{
        position: "absolute", left: "58%", top: "52%", width: "38%", height: "30%",
        borderRadius: "0 0 50% 50%", borderBottom: `4px solid ${t.glow}`,
        borderLeft: `3px solid ${t.main}88`, borderRight: `3px solid ${t.main}88`,
        boxShadow: `0 0 8px ${t.glow}66`,
        animation: "anniFxJawBot 0.32s ease-in forwards",
      }} />
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{
          position: "absolute", left: `${68 + (i % 2) * 12}%`, top: `${40 + i * 5}%`,
          width: 4, height: 4, borderRadius: "50%", background: t.glow,
          boxShadow: `0 0 5px ${t.glow}`, "--ex": `${(i - 1.5) * 9}px`,
          animation: `anniFxEmber 0.34s ease-out forwards`, animationDelay: `${i * 0.03}s`,
        }} />
      ))}
    </div>
  );
}

// QUAN-7 — Blade Cycle. A clockwork sword arc that CHANGES per
// step: high slash, return slash, straight thrust. Gear glint.
function BladeCycleFX({ ev, tile }) {
  const t = ev.tint || DEFAULT_TINT;
  const size = tile * 2.6;
  const thrust = ev.step >= 3;
  const reverse = ev.step === 2;
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), transform: `rotate(${ev.angle}rad)` }}>
      {thrust ? (
        <div style={{
          position: "absolute", left: "54%", top: "46%", width: "44%", height: "8%",
          borderRadius: 999, transformOrigin: "0% 50%",
          background: `linear-gradient(90deg, ${t.accent}00, ${t.accent}, ${t.glow})`,
          boxShadow: `0 0 7px ${t.glow}`, animation: "anniFxBeam 0.24s ease-out forwards",
        }} />
      ) : (
        <div style={{
          position: "absolute", left: "34%", top: "18%", width: "64%", height: "64%",
          borderRadius: "50%", transformOrigin: "18% 50%",
          borderTop: `3px solid ${t.glow}`, borderRight: `3px solid ${t.accent}`,
          borderBottom: "3px solid transparent", borderLeft: "3px solid transparent",
          filter: `drop-shadow(0 0 5px ${t.glow})`,
          "--a0": reverse ? "60deg" : "-150deg", "--a1": reverse ? "-150deg" : "60deg",
          animation: "anniFxSweep 0.3s ease-out forwards",
        }} />
      )}
      <div style={{
        position: "absolute", left: "60%", top: "40%", width: 5, height: 5,
        borderRadius: "50%", background: t.glow, boxShadow: `0 0 6px ${t.glow}`,
        animation: "anniFxSpark 0.3s ease-out forwards",
      }} />
    </div>
  );
}

// OS'ORUN-VETH — Psychic Lance. A violet beam extends from the
// vessel; a ring blooms at the tip. Ranged in spirit, honest to
// the current melee-reach mechanics.
function PsychicLanceFX({ ev, tile }) {
  const t = ev.tint || DEFAULT_TINT;
  const size = tile * 3.0;
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), transform: `rotate(${ev.angle}rad)` }}>
      <div style={{
        position: "absolute", left: "52%", top: "46%", width: "44%", height: "8%",
        borderRadius: 999, transformOrigin: "0% 50%",
        background: `linear-gradient(90deg, ${t.main}00, ${t.main}, ${t.glow})`,
        boxShadow: `0 0 9px ${t.main}`, animation: "anniFxBeam 0.3s ease-out forwards",
      }} />
      <div style={{
        position: "absolute", left: "88%", top: "38%", width: "22%", height: "22%",
        borderRadius: "50%", border: `2px solid ${t.glow}`,
        boxShadow: `0 0 8px ${t.glow}`, animation: "anniFxRing 0.32s ease-out forwards",
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// UNIVERSAL PLAYER FX
// ─────────────────────────────────────────────────────────────

// Dodge — three afterimages along the dash line, YOUR colors.
function DodgeStreakFX({ ev, tile }) {
  if (!ev.to) return null;
  const t = ev.tint || DEFAULT_TINT;
  return (
    <>
      {[0.25, 0.55, 0.85].map((f, i) => {
        const p = {
          x: ev.pos.x + (ev.to.x - ev.pos.x) * f,
          y: ev.pos.y + (ev.to.y - ev.pos.y) * f,
        };
        return (
          <div key={i} style={{
            ...boxAt(p, tile, tile * 0.8), borderRadius: "50%",
            background: `radial-gradient(circle, ${t.main}55 0%, transparent 70%)`,
            animation: "anniFxGhost 0.38s linear forwards", animationDelay: `${i * 0.04}s`,
          }} />
        );
      })}
    </>
  );
}

// Lyt dry — the breather, visible. Two grey pants.
function LytDryFX({ ev, tile }) {
  return (
    <div style={boxAt(ev.pos, tile, tile)}>
      {[0, 1].map((i) => (
        <div key={i} style={{
          position: "absolute", left: `${30 + i * 26}%`, top: "8%",
          width: "22%", height: "22%", borderRadius: "50%",
          background: "radial-gradient(circle, #9fa8b499 0%, transparent 70%)",
          animation: "anniFxPuff 0.5s ease-out forwards", animationDelay: `${i * 0.1}s`,
        }} />
      ))}
    </div>
  );
}

// Impact spark on a struck target — crits flare gold and bigger.
function HitSparkFX({ ev, tile }) {
  const gold = ev.crit;
  const size = tile * (gold ? 1.5 : 1.05) * (ev.big ? 1.3 : 1);
  const c = gold ? "#ffd24a" : (ev.tint ? ev.tint.glow : "#fff2c9");
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), animation: "anniFxSpark 0.3s ease-out forwards" }}>
      <div style={{
        position: "absolute", inset: "34%", borderRadius: "50%",
        background: `radial-gradient(circle, ${c} 0%, transparent 70%)`,
      }} />
      {[0, 60, 120].map((deg) => (
        <div key={deg} style={{
          position: "absolute", left: "10%", top: "48%", width: "80%", height: "4%",
          borderRadius: 999, background: `linear-gradient(90deg, transparent, ${c}, transparent)`,
          transform: `rotate(${deg}deg)`,
        }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ENEMY FX — the field roster's swings, distinct per ability.
// ─────────────────────────────────────────────────────────────

// pounce — wolf/fox lunge: a grey motion streak + closing fangs.
function PounceFX({ ev, tile }) {
  const size = tile * 2.2;
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), transform: `rotate(${ev.angle}rad)` }}>
      <div style={{
        position: "absolute", left: "10%", top: "44%", width: "72%", height: "12%",
        borderRadius: 999, transformOrigin: "0% 50%",
        background: "linear-gradient(90deg, transparent, #b8c2cc99, #e8eef4bb)",
        animation: "anniFxStreak 0.3s ease-out forwards",
      }} />
      <div style={{
        position: "absolute", left: "72%", top: "30%", width: "18%", height: "16%",
        clipPath: "polygon(0 0, 50% 100%, 100% 0)", background: "#e8eef4",
        animation: "anniFxJawTop 0.3s ease-in forwards",
      }} />
      <div style={{
        position: "absolute", left: "72%", top: "54%", width: "18%", height: "16%",
        clipPath: "polygon(0 100%, 50% 0, 100% 100%)", background: "#e8eef4",
        animation: "anniFxJawBot 0.3s ease-in forwards",
      }} />
    </div>
  );
}

// gore — the corrupted antler rake: two crossing crimson slashes
// that drip. The Antlered One throws it bigger (big flag).
function GoreFX({ ev, tile }) {
  const size = tile * (ev.big ? 3.2 : 2.3);
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), transform: `rotate(${ev.angle}rad)` }}>
      {[-22, 22].map((deg, i) => (
        <div key={i} style={{
          position: "absolute", left: "40%", top: `${36 + i * 16}%`, width: "56%", height: "9%",
          borderRadius: 999, transformOrigin: "0% 50%", transform: `rotate(${deg}deg)`,
          background: "linear-gradient(90deg, transparent, #a83232, #e85c5c)",
          boxShadow: "0 0 6px #a8323288",
          animation: "anniFxRake 0.34s ease-out forwards",
        }} />
      ))}
      {[0, 1, 2].map((i) => (
        <div key={`d${i}`} style={{
          position: "absolute", left: `${58 + i * 12}%`, top: "56%",
          width: 4, height: 6, borderRadius: "0 0 50% 50%", background: "#c93c3c",
          animation: "anniFxDrip 0.42s ease-in forwards", animationDelay: `${0.08 + i * 0.05}s`,
        }} />
      ))}
      {ev.big ? (
        <div style={{
          position: "absolute", inset: "20%", borderRadius: "50%",
          border: "2px solid #c9a4e866", boxShadow: "0 0 10px #c9a4e844",
          animation: "anniFxRing 0.45s ease-out forwards",
        }} />
      ) : null}
    </div>
  );
}

// dummy_sweep — Sigfer's dummy: a wooden arm swings through,
// trailing sawdust. Even the training post commits.
function DummySweepFX({ ev, tile }) {
  const size = tile * 2.4;
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), transform: `rotate(${ev.angle}rad)` }}>
      <div style={{
        position: "absolute", left: "48%", top: "46%", width: "46%", height: "9%",
        borderRadius: 3, transformOrigin: "0% 50%",
        background: "linear-gradient(90deg, #6d4f2f, #a8823f)",
        boxShadow: "0 0 4px rgba(0,0,0,0.4)",
        "--a0": "-100deg", "--a1": "80deg",
        animation: "anniFxSweep 0.4s ease-in-out forwards",
      }} />
      {[0, 1].map((i) => (
        <div key={i} style={{
          position: "absolute", left: `${56 + i * 16}%`, top: "36%",
          width: "12%", height: "12%", borderRadius: "50%",
          background: "radial-gradient(circle, #c9b48a77 0%, transparent 70%)",
          animation: "anniFxPuff 0.42s ease-out forwards", animationDelay: `${i * 0.08}s`,
        }} />
      ))}
    </div>
  );
}

// phantom_swipe — the Prowler's claw: one thin teal rake, half
// in the other world.
function PhantomSwipeFX({ ev, tile }) {
  const size = tile * 2.2;
  return (
    <div style={{ ...boxAt(ev.pos, tile, size), transform: `rotate(${ev.angle}rad)` }}>
      <div style={{
        position: "absolute", left: "42%", top: "44%", width: "54%", height: "10%",
        borderRadius: 999, transformOrigin: "0% 50%", filter: "blur(0.8px)",
        background: "linear-gradient(90deg, transparent, #7fe8d8bb, #b8fff2)",
        boxShadow: "0 0 8px #7fe8d877",
        animation: "anniFxRake 0.28s ease-out forwards",
      }} />
      <div style={{
        position: "absolute", left: "40%", top: "30%", width: "56%", height: "40%",
        borderRadius: "50%", background: "radial-gradient(ellipse, #7fe8d822 0%, transparent 70%)",
        animation: "anniFxGhost 0.3s linear forwards",
      }} />
    </div>
  );
}

// phase_shimmer — a Prowler crossing the veil, either direction.
function PhaseShimmerFX({ ev, tile }) {
  return (
    <div style={boxAt(ev.pos, tile, tile * 1.6)}>
      <div style={{
        position: "absolute", inset: "8%", borderRadius: "50%",
        border: "2px solid #7fe8d8aa", boxShadow: "0 0 10px #7fe8d866",
        animation: "anniFxRing 0.5s ease-out forwards",
      }} />
      <div style={{
        position: "absolute", inset: "26%", borderRadius: "50%", filter: "blur(2px)",
        background: "radial-gradient(circle, #b8fff244 0%, transparent 70%)",
        animation: "anniFxGhost 0.5s linear forwards",
      }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// THE MATRIARCH — the strike itself, one recipe per attack.
// (Her wind-up tells stay in the dungeon file — a telegraph is
// arena furniture; the STRIKE is an ability, so it lives here.)
// ─────────────────────────────────────────────────────────────

// Phantom Lunge — the dash made visible: a teal streak from
// where she was to where you were, and a slam ring on arrival.
function PhantomLungeFX({ ev, tile }) {
  if (!ev.to) return null;
  const len = Math.max(1, Math.hypot(ev.to.x - ev.pos.x, ev.to.y - ev.pos.y)) * tile;
  const ang = Math.atan2(ev.to.y - ev.pos.y, ev.to.x - ev.pos.x);
  return (
    <>
      <div style={{
        position: "absolute",
        left: ev.pos.x * tile + tile / 2, top: ev.pos.y * tile + tile / 2 - 7,
        width: len, height: 14, transform: `rotate(${ang}rad)`, transformOrigin: "0 50%",
        pointerEvents: "none", zIndex: 6,
      }}>
        <div style={{
          position: "absolute", inset: 0, borderRadius: 999, transformOrigin: "0% 50%",
          background: "linear-gradient(90deg, transparent, #7fe8d8aa, #b8fff2)",
          boxShadow: "0 0 12px #7fe8d888", filter: "blur(0.5px)",
          animation: "anniFxStreak 0.4s ease-out forwards",
        }} />
      </div>
      <div style={{
        ...boxAt(ev.to, tile, tile * 2), borderRadius: "50%",
        border: "3px solid #b8fff2aa", boxShadow: "0 0 14px #7fe8d877",
        animation: "anniFxRing 0.42s ease-out forwards",
      }} />
    </>
  );
}

// Claw Sweep — a full circle of phantom rakes wheeling around
// her, matching the 2.2-tile hit radius.
function ClawSweepFX({ ev, tile }) {
  const size = tile * 4.4;
  return (
    <div style={boxAt(ev.pos, tile, size)}>
      <div style={{
        position: "absolute", inset: 0, transformOrigin: "50% 50%",
        "--a0": "0deg", "--a1": "300deg",
        animation: "anniFxSweep 0.55s ease-out forwards",
      }}>
        {[0, 120, 240].map((deg) => (
          <div key={deg} style={{
            position: "absolute", left: "50%", top: "50%", width: "46%", height: "5%",
            borderRadius: 999, transformOrigin: "0% 50%", transform: `rotate(${deg}deg)`,
            background: "linear-gradient(90deg, transparent 20%, #7fe8d8bb, #dffcf6)",
            boxShadow: "0 0 8px #7fe8d877",
          }} />
        ))}
      </div>
      <div style={{
        position: "absolute", inset: "4%", borderRadius: "50%",
        border: "2px solid #7fe8d855", animation: "anniFxRing 0.55s ease-out forwards",
      }} />
    </div>
  );
}

// Spectral Roar — the cone made audible: expanding shock rings
// clipped to her ~40° aim.
function SpectralRoarFX({ ev, tile }) {
  const size = tile * 7;
  return (
    <div style={{
      position: "absolute",
      left: ev.pos.x * tile + tile / 2, top: ev.pos.y * tile + tile / 2 - size / 2,
      width: size, height: size, pointerEvents: "none", zIndex: 6,
      transform: `rotate(${ev.angle}rad)`, transformOrigin: "0 50%",
      clipPath: "polygon(0 50%, 100% 6%, 100% 94%)",
    }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{
          position: "absolute", left: -size / 2, top: 0, width: size, height: size,
          borderRadius: "50%", border: "3px solid #c9a4e8aa",
          boxShadow: "0 0 12px #c9a4e855",
          animation: "anniFxRing 0.6s ease-out forwards", animationDelay: `${i * 0.12}s`,
        }} />
      ))}
    </div>
  );
}

// Haunting Presence — the crypt itself dims: a violet pulse the
// width of the arena, shadow falling everywhere but the light.
function HauntingPresenceFX({ ev, tile }) {
  const size = tile * 13;
  return (
    <div style={boxAt(ev.pos, tile, size)}>
      <div style={{
        position: "absolute", inset: 0, borderRadius: "50%",
        background: "radial-gradient(circle, #c9a4e844 0%, #1c103855 45%, transparent 70%)",
        animation: "anniFxPulse 0.85s ease-out forwards",
      }} />
      {[14, 30, 46, 62, 78].map((l, i) => (
        <div key={i} style={{
          position: "absolute", left: `${l}%`, top: `${18 + (i % 3) * 12}%`,
          width: 5, height: 9, borderRadius: "0 0 50% 50%", background: "#c9a4e8",
          boxShadow: "0 0 6px #c9a4e8",
          animation: "anniFxDrip 0.8s ease-in forwards", animationDelay: `${i * 0.07}s`,
        }} />
      ))}
    </div>
  );
}

// safe_light — the one lit tile, holding. The counter, glowing.
function SafeLightFX({ ev, tile }) {
  return (
    <div style={{
      position: "absolute",
      left: ev.pos.x * tile + tile * 0.1, top: ev.pos.y * tile - tile * 0.8,
      width: tile * 0.8, height: tile * 1.7, pointerEvents: "none", zIndex: 6,
      background: "linear-gradient(180deg, transparent, #ffd24a55 60%, #ffd24a88)",
      borderRadius: "40% 40% 20% 20%", filter: "blur(1px)",
      animation: "anniFxColumn 0.85s ease-out forwards",
    }} />
  );
}

// pillar_shatter — the Phase II break: stone flying, dust ring.
function PillarShatterFX({ ev, tile }) {
  const size = tile * 2.2;
  const shards = [
    { sx: 14, sy: -18 }, { sx: -12, sy: -20 }, { sx: 20, sy: -6 },
    { sx: -18, sy: -8 }, { sx: 8, sy: -26 }, { sx: -6, sy: -24 },
  ];
  return (
    <div style={boxAt(ev.pos, tile, size)}>
      {shards.map((s, i) => (
        <div key={i} style={{
          position: "absolute", left: "46%", top: "46%", width: 6, height: 8,
          background: "linear-gradient(160deg, #8a93a8, #4a4f5c)", borderRadius: 1,
          "--sx": `${s.sx}px`, "--sy": `${s.sy}px`,
          animation: "anniFxShard 0.55s ease-out forwards", animationDelay: `${i * 0.03}s`,
        }} />
      ))}
      <div style={{
        position: "absolute", inset: "18%", borderRadius: "50%",
        border: "2px solid #8a93a866", animation: "anniFxRing 0.6s ease-out forwards",
      }} />
    </div>
  );
}

// soul_absorb — a cub reaching home: green motes rising into her.
function SoulAbsorbFX({ ev, tile }) {
  return (
    <div style={boxAt(ev.pos, tile, tile * 1.6)}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{
          position: "absolute", left: `${22 + i * 18}%`, top: "58%",
          width: 5, height: 5, borderRadius: "50%", background: "#8fd18a",
          boxShadow: "0 0 6px #8fd18a",
          animation: "anniFxRise 0.68s ease-out forwards", animationDelay: `${i * 0.09}s`,
        }} />
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// FX-1 — SAEL'ESH'S CASTS (2D recipes). The 3D field renders
// these through fold3dKit's AbilityWorldFX; these keep the
// July 16 continuity law alive on every 2D surface (Sigfer's
// Yard, rollback paths): the move you cast is the move you
// recognize, in either dimension. haunting_presence is NOT
// re-keyed here — the player's U'hlti fires the Matriarch's
// existing entry, exactly as the registry header promised.
// ─────────────────────────────────────────────────────────────

// Spectral Paws — the coil. A ground ripple and paw-fire flare.
function SpectralPawsFX({ ev, tile }) {
  const t = ev.tint || DEFAULT_TINT;
  const size = tile * 2.2;
  return (
    <div style={boxAt(ev.pos, tile, size)}>
      <div style={{
        position: "absolute", inset: "22%", borderRadius: "50%",
        border: `3px solid ${t.main}`, boxShadow: `0 0 10px ${t.main}88`,
        animation: "anniFxRing 0.6s ease-out forwards",
      }} />
      {[-1, 1].map((d, i) => (
        <div key={i} style={{
          position: "absolute", left: `${46 + d * 10}%`, top: "40%",
          width: "10%", height: "10%", borderRadius: "50%",
          background: `radial-gradient(circle, ${t.glow} 0%, transparent 70%)`,
          animation: "anniFxSpark 0.5s ease-out forwards", animationDelay: `${i * 0.05}s`,
        }} />
      ))}
      {[0, 1, 2].map((i) => (
        <div key={`p${i}`} style={{
          position: "absolute", left: `${38 + i * 11}%`, top: "48%",
          width: 7, height: 7, borderRadius: "50%", background: `${t.main}66`,
          animation: "anniFxPuff 0.6s ease-out forwards", animationDelay: `${i * 0.06}s`,
        }} />
      ))}
    </div>
  );
}

// Spectral Claws — one seeking streak per claw (spawned per
// target by the zone), ending in a rake burst at the prey.
function SpectralClawsFX({ ev, tile }) {
  const t = ev.tint || DEFAULT_TINT;
  if (!ev.to) return null;
  const dx = (ev.to.x - ev.pos.x) * tile;
  const dy = (ev.to.y - ev.pos.y) * tile;
  const len = Math.hypot(dx, dy);
  const ang = Math.atan2(dy, dx);
  return (
    <>
      <div style={{
        position: "absolute",
        left: ev.pos.x * tile + tile / 2, top: ev.pos.y * tile + tile / 2 - 3,
        width: len, height: 6, transformOrigin: "0% 50%",
        transform: `rotate(${ang}rad)`, borderRadius: 999,
        background: `linear-gradient(90deg, transparent, ${t.main}, ${t.glow})`,
        boxShadow: `0 0 8px ${t.glow}88`, pointerEvents: "none", zIndex: 6,
        animation: "anniFxStreak 0.4s ease-in forwards",
      }} />
      <div style={{ ...boxAt(ev.to, tile, tile * 1.6) }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{
            position: "absolute", left: "18%", top: `${26 + i * 22}%`,
            width: "64%", height: "10%", borderRadius: 999,
            background: `linear-gradient(90deg, transparent, ${t.glow}, ${t.main})`,
            boxShadow: `0 0 6px ${t.glow}`, transform: `rotate(${(i - 1) * 16}deg)`,
            animation: "anniFxRake 0.32s ease-out forwards", animationDelay: "0.28s",
            opacity: 0,
          }} />
        ))}
      </div>
    </>
  );
}

// Ghost Form — the crossing: an expanding spectral shell + the
// ghost fade, right where the Tiger stands.
function GhostFormFX({ ev, tile }) {
  const t = ev.tint || DEFAULT_TINT;
  const size = tile * 2.8;
  return (
    <div style={boxAt(ev.pos, tile, size)}>
      <div style={{
        position: "absolute", inset: "14%", borderRadius: "50%",
        border: `3px solid ${t.main}aa`, boxShadow: `0 0 14px ${t.main}66`,
        animation: "anniFxRing 0.8s ease-out forwards",
      }} />
      <div style={{
        position: "absolute", inset: "24%", borderRadius: "50%",
        background: `radial-gradient(circle, ${t.main}44 0%, transparent 70%)`,
        animation: "anniFxGhost 0.9s linear forwards",
      }} />
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{
          position: "absolute", left: `${28 + i * 15}%`, top: "52%",
          width: 8, height: 8, borderRadius: "50%", background: `${t.glow}88`,
          animation: "anniFxRise 0.8s ease-out forwards", animationDelay: `${i * 0.08}s`,
        }} />
      ))}
    </div>
  );
}

// Nine Lives — the Grace spent: a golden ring and nine embers
// scattering from the death-spot as the Tiger reforms.
function NineLivesFX({ ev, tile }) {
  const t = ev.tint || DEFAULT_TINT;
  const size = tile * 3;
  return (
    <div style={boxAt(ev.pos, tile, size)}>
      <div style={{
        position: "absolute", inset: "16%", borderRadius: "50%",
        border: "4px solid #ffd24a", boxShadow: "0 0 16px #ffd24a88",
        animation: "anniFxRing 0.7s ease-out forwards",
      }} />
      {Array.from({ length: 9 }).map((_, i) => {
        const a = (i / 9) * Math.PI * 2;
        return (
          <div key={i} style={{
            position: "absolute", left: "47%", top: "47%",
            width: 7, height: 7, borderRadius: "50%",
            background: i % 2 ? "#ffd24a" : t.glow,
            boxShadow: `0 0 6px ${i % 2 ? "#ffd24a" : t.glow}`,
            "--sx": `${Math.cos(a) * 34}px`, "--sy": `${Math.sin(a) * 34 - 12}px`,
            animation: "anniFxShard 0.9s ease-out forwards", animationDelay: `${i * 0.03}s`,
          }} />
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// THE REGISTRY — internal ability id → recipe. Keys never
// change (vyren); recipes retune freely.
// ─────────────────────────────────────────────────────────────
const FX_DEFS = {
  // player Lyt attacks (beings.js lytAttack ids)
  phantom_strike: { ttlMs: 340, render: PhantomStrikeFX },
  dragons_bite: { ttlMs: 400, render: DragonsBiteFX },
  blade_cycle: { ttlMs: 340, render: BladeCycleFX },
  psychic_lance: { ttlMs: 360, render: PsychicLanceFX },
  // FX-1 — Sael'esh's casts (haunting_presence reuses HER key above)
  spectral_paws: { ttlMs: 700, render: SpectralPawsFX },
  spectral_claws: { ttlMs: 720, render: SpectralClawsFX },
  ghost_form: { ttlMs: 950, render: GhostFormFX },
  nine_lives: { ttlMs: 980, render: NineLivesFX },
  // universal player feel
  dodge_streak: { ttlMs: 460, render: DodgeStreakFX },
  lyt_dry: { ttlMs: 620, render: LytDryFX },
  hit_spark: { ttlMs: 340, render: HitSparkFX },
  // field enemies (enemyTaxonomy abilities)
  pounce: { ttlMs: 360, render: PounceFX },
  gore: { ttlMs: 500, render: GoreFX },
  dummy_sweep: { ttlMs: 480, render: DummySweepFX },
  phantom_swipe: { ttlMs: 340, render: PhantomSwipeFX },
  phase_shimmer: { ttlMs: 540, render: PhaseShimmerFX },
  // the Matriarch (BOSSES.phantom_matriarch.attacks ids)
  phantom_lunge: { ttlMs: 460, render: PhantomLungeFX },
  claw_sweep: { ttlMs: 620, render: ClawSweepFX },
  spectral_roar: { ttlMs: 820, render: SpectralRoarFX },
  haunting_presence: { ttlMs: 950, render: HauntingPresenceFX },
  safe_light: { ttlMs: 900, render: SafeLightFX },
  // arena moments
  pillar_shatter: { ttlMs: 640, render: PillarShatterFX },
  soul_absorb: { ttlMs: 760, render: SoulAbsorbFX },
};
